/**
 * MKS-349 follow-up (2026-10-18) — "show what will change per row".
 *
 * Petra reviewed the first staging run without being able to see, per row,
 * which of the three fields the confirm would actually write. The counts said
 * "5 exact" and the table said which invoice each row paired with, but not
 * whether confirming would set a number, move a due date, or — with the new
 * option on — unpay an invoice.
 *
 * A pure module rather than JSX in the page, for two reasons: it mirrors
 * `confirmImportPairs` field for field (the preview is worthless if it drifts
 * from what the server writes), and that correspondence is only testable if it
 * can be called without rendering a table.
 */
import { formatDisplayDate } from '../../utils/format';
import type { ImportInvoiceView, MatchRow } from '../../services/api/invoiceImport';

export interface ChangePreview {
  /** `number`, `due` or `paid` — the operator-facing name of the field. */
  label: string;
  /** What the Monksheets invoice holds now, already formatted. */
  from: string;
  /** What confirming would write, already formatted. */
  to: string;
}

/** The em dash the rest of the invoice surfaces use for "nothing here". */
const EMPTY = '–';

function dateText(value: string | null | undefined): string {
  return value ? formatDisplayDate(value) : EMPTY;
}

/**
 * What confirming `row` against `invoice` would change.
 *
 * Deliberately a DIFF, not a summary: a field the confirm would leave alone is
 * absent from the list, so a row showing only `number: – → 2026100102` is a
 * promise that the due date and the payment are not being touched.
 *
 * The three rules mirror `confirmImportPairs` exactly:
 *   - the number is written when it differs (the server refuses outright when
 *     the invoice already carries a different one — see `conflict`);
 *   - a due date in the file is written when it differs; an absent one changes
 *     nothing;
 *   - a paid date in the file is written when it differs; an absent one
 *     changes nothing UNLESS `markUnpaidWhenNoPaidDate` is on, in which case a
 *     paid invoice is unpaid.
 */
export function describeRowChanges(
  row: Pick<MatchRow, 'invoiceNumber' | 'dueDate' | 'paidDate'>,
  invoice: Pick<ImportInvoiceView, 'invoiceNumber' | 'dueDate' | 'paidAt'> | undefined,
  options: { markUnpaidWhenNoPaidDate: boolean }
): ChangePreview[] {
  if (!invoice) return [];
  const changes: ChangePreview[] = [];

  const currentNumber = invoice.invoiceNumber?.trim() ?? '';
  const nextNumber = row.invoiceNumber?.trim() ?? '';
  if (nextNumber !== '' && nextNumber !== currentNumber) {
    changes.push({
      label: 'number',
      from: currentNumber === '' ? EMPTY : currentNumber,
      to: nextNumber,
    });
  }

  if (row.dueDate && row.dueDate !== invoice.dueDate) {
    changes.push({ label: 'due', from: dateText(invoice.dueDate), to: dateText(row.dueDate) });
  }

  if (row.paidDate) {
    if (row.paidDate !== invoice.paidAt) {
      changes.push({ label: 'paid', from: dateText(invoice.paidAt), to: dateText(row.paidDate) });
    }
  } else if (options.markUnpaidWhenNoPaidDate && invoice.paidAt) {
    changes.push({ label: 'paid', from: dateText(invoice.paidAt), to: 'unpaid' });
  }

  return changes;
}

/** `number: – → 2026100102, paid: 20.10.2026 → unpaid`, for a one-line cell. */
export function formatChangePreview(changes: ChangePreview[]): string {
  if (changes.length === 0) return 'Nothing to change';
  return changes.map((change) => `${change.label}: ${change.from} → ${change.to}`).join(', ');
}
